import {
  SCRIPT_LOADED,
  SDK_INITIALIZED,
  pageViewRequestSend,
  scriptLoaded,
  sdkInitialized,
} from '../store/modules/googleTags'
import { combineEpics, ofType } from 'redux-observable'
import {
  filter,
  ignoreElements,
  map,
  mergeMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs'
import { isWebView } from '../utilities/displays'
import { logObservableError } from '../utilities/logs'
import { AUTHORIZE_NON_IAB_VENDORS } from '../store/modules/consentManagement'
import { TITLE_CHANGED } from '../store/modules/metas'
import { FETCH } from '../store/modules/search'
import { BRIGHTCOVE_VIDEO_PLAYED, INIT_BRIGHTCOVE_VIDEO } from '../store/modules/brightcoveVideo'
import { isArticleRoute, isCategoryRoute, isNewsFeedRoute } from '../utilities/urls'
import { guessVideoPlayerId } from '../legacy_components/BrightcovePlayers/BrightcoveVideoPlayer'
import { CHANGE_LANGUAGE } from '../store/modules/router'
import { BOOKMARK_ADDED, ERROR, OPEN_SIGN_IN, SIGNED_IN, SIGNED_OUT, SIGNED_UP, UNVERIFIED_ERROR } from '../store/modules/session'
import { ERROR as CONTACT_ERROR, SUBMITTED } from '../store/modules/contact'
import { SUBMITTED as NEWSLETTER_SUBMITTED, ERROR as NEWSLETTER_ERROR } from '../store/modules/newsletter'
import { DOWNLOAD_ANDROID_APP, DOWNLOAD_IOS_APP } from '../store/modules/mobileApps'
import { SHARE_ON_SOCIAL } from '../store/modules/social'
import { guessLiveVideoPlayerId } from '../legacy_components/BrightcovePlayers/BrightcoveLiveVideoPlayer'
import { RECEIVE_NEW_COMMENT } from '../store/modules/comments'
import { DOWNLOAD_PDF } from '../store/modules/legal'

// getContentType :: String -> String
const getContentType = routeName => routeName ? routeName.replace(/(Fr|Ar|En|He|Fa)$/, '') : ''

// getContentTitle :: State -> String
const getContentTitle = state => {
  if (isArticleRoute(state.router.activeRoute.name)) {
    return state.article?.content?.title ?? ''
  }
  if (isCategoryRoute(state.router.activeRoute.name)) {
    return state.category?.category?.name ?? ''
  }
  if (isNewsFeedRoute(state.router.activeRoute.name)) {
    return state.newsFeed.newsPages?.[1]?.[0]?.title ?? ''
  }

  return state.metas.title
}

// getContentId :: State -> String | Null
const getContentId = state => {
  if (isArticleRoute(state.router.activeRoute.name)) {
    return state.article.content.id
  }
  if (isCategoryRoute(state.router.activeRoute.name)) {
    return state.category.category.id
  }
  if (isNewsFeedRoute(state.router.activeRoute.name)) {
    return state.newsFeed.newsPages?.[1]?.[0]?.id ?? null
  }

  return null
}

// getCategoryName :: State -> String | Null
const getCategoryName = state => {
  if (isArticleRoute(state.router.activeRoute.name)) {
    return state.article.content.category.name
  }
  if (isCategoryRoute(state.router.activeRoute.name)) {
    return state.category.category.name
  }

  return null
}

// getParentCategoryName :: State -> String | Null
const getParentCategoryName = state => {
  if (isArticleRoute(state.router.activeRoute.name)) {
    return state.article.content.category.parent?.name ?? null
  }
  if (isCategoryRoute(state.router.activeRoute.name)) {
    return state.category.category.parent?.name ?? null
  }

  return null
}

// getVideoTitle :: (State, String) -> String | Null
const getVideoTitle = (state, videoId) => {
  const routeName = state.router.activeRoute.name

  if (routeName === 'homepage') {
    const i24NewsPlusVideo = state.homepage.i24NewsPlusVideos.list.find(v => v.id === videoId)
    if (i24NewsPlusVideo) return i24NewsPlusVideo.name

    const topArticles = state.homepage.topArticles.list.find(v => v.id === videoId)
    if (topArticles) return topArticles.name
  }

  if (isArticleRoute(routeName)) {
    if (state.article.content.videoCover?.id === videoId) {
      return state.article.content.videoCover.description
    }

    const videoComponent = state.article.content.components?.find(c => c.type === 'brightcoveVideo' && c.id === videoId)

    if (videoComponent) return videoComponent.description
  }

  return null
}

// getVideoDuration :: (State, String) -> Number | Null
const getVideoDuration = (state, videoId) => {
  const routeName = state.router.activeRoute.name

  if (routeName === 'homepage') {
    const i24NewsPlusVideo = state.homepage.i24NewsPlusVideos.list.find(v => v.id === videoId)
    if (i24NewsPlusVideo) return i24NewsPlusVideo.duration

    const topArticles = state.homepage.topArticles.list.find(v => v.id === videoId)
    if (topArticles) return topArticles.duration
  }

  return null
}

// isLiveVideo :: String -> Boolean
const isLiveVideo = videoId => [
  process.env.REACT_APP_BRIGHTCOVE_LIVE_STREAM_VIDEO_ID_AR,
  process.env.REACT_APP_BRIGHTCOVE_LIVE_STREAM_VIDEO_ID_FR,
  process.env.REACT_APP_BRIGHTCOVE_LIVE_STREAM_VIDEO_ID_EN,
  process.env.REACT_APP_BRIGHTCOVE_LIVE_STREAM_VIDEO_ID_HE,
].includes(videoId)

// loadGtagScriptEpic :: Epic -> Observable Action SCRIPT_LOADED
export const loadGtagScriptEpic = (action$, state$, { document, logger, location }) =>
  action$.pipe(
    ofType(AUTHORIZE_NON_IAB_VENDORS),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    take(1),
    mergeMap(() => new Promise(resolve => {
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID}`
      script.onload = () => resolve()

      document.querySelector('head').appendChild(script)
    })),
    map(scriptLoaded),
    logObservableError('googleTags :: loadGtagScriptEpic', logger),
  )

// @see https://support.google.com/analytics/answer/1008080
//
// initializeGtagEpic :: Epic -> Observable Action SDK_INITIALIZED
export const initializeGtagEpic = (action$, state$, { logger, window }) =>
  action$.pipe(
    ofType(SCRIPT_LOADED),
    tap(() => {
      window.dataLayer = window.dataLayer || []
      window.gtag = function () {
        window.dataLayer.push(arguments)
      }
      window.gtag('js', new Date())
      window.gtag('config', process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID)
    }),
    map(sdkInitialized),
    logObservableError('googleTags :: initializeGtagEpic', logger),
  )

// pageViewEpic :: Epic -> Observable Action PAGE_VIEW_REQUEST_SEND
export const pageViewEpic = (action$, state$, { window, dataLayer, gtag, location, logger }) =>
  action$.pipe(
    ofType(SDK_INITIALIZED, TITLE_CHANGED),
    withLatestFrom(state$),
    filter(() => !isWebView(location)),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(([ _, state ]) => location.pathname !== state.googleTags.lastPageViewed),
    filter(() => gtag() && dataLayer()),
    tap(([ _, state ]) => gtag()(
      'config',
      process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
      {
        'page_path': location.pathname,
        'page_title': state.metas.title,
      },
    )),
    tap(([ _, state ]) => dataLayer().push({
      event: 'Pageview',
      url: location.href,
      content_type: getContentType(state.router.activeRoute.name),
      content_title: getContentTitle(state),
      content_id: getContentId(state),
      author: isArticleRoute(state.router.activeRoute.name) ? state.article.content.signatures[0]?.authorName : null,
      category_level_1: getCategoryName(state),
      category_level_2: getParentCategoryName(state),
      category_level_3: null,
      publish_date: isArticleRoute(state.router.activeRoute.name) ? state.article.content.publishedAt : null,
      user_id: state.session?.user?.id ?? '',
      comment_count: isArticleRoute(state.router.activeRoute.name) ? state.article.content.numberOfComments : null,
    })),
    map(() => pageViewRequestSend(location.pathname)),
    logObservableError('googleTags :: pageViewEpic', logger),
  )

// searchEpic :: Epic -> Observable Action _
export const searchEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(FETCH),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    filter(([ _, state ]) => state.search.filters.keyword !== ''),
    tap(([ _, state ]) => dataLayer().push({
      event: 'dl_event',
      eventName: 'search',
      user_id: state.session?.user?.id ?? '',
      search_term: state.search.filters.keyword,
    })),
    ignoreElements(),
    logObservableError('googleTags :: searchEpic', logger),
  )

// videoPlayRequestEpic :: Epic -> Observable Action _
export const videoPlayRequestEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(INIT_BRIGHTCOVE_VIDEO),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(([{ videoId }, _]) => !isLiveVideo(videoId)),
    filter(() => dataLayer()),
    tap(([{ videoId }, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'video_play_request',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: getContentTitle(state),
        content_id: getContentId(state),
        author: isArticleRoute(state.router.activeRoute.name) ? state.article.content.signatures[0]?.authorName : null,
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        duration: getVideoDuration(state, videoId),
        element_id: guessVideoPlayerId(state.router.activeRoute.locale),
        element_location: null,
        element_type: null,
        publish_date: isArticleRoute(state.router.activeRoute.name) ? state.article.content.publishedAt : null,
        element_title: getVideoTitle(state, videoId),
        user_id: state.session?.user?.id ?? '',
        video_id: videoId,
        element_provider: 'Brightcove',
        comment_count: isArticleRoute(state.router.activeRoute.name) ? state.article.content.numberOfComments : null,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: videoPlayRequestEpic', logger),
  )

// videoStartEpic :: Epic -> Observable Action _
export const videoStartEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(BRIGHTCOVE_VIDEO_PLAYED),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(([{ videoId }, _]) => !isLiveVideo(videoId)),
    filter(() => dataLayer()),
    tap(([{ videoId }, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'video_start',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: getContentTitle(state),
        content_id: getContentId(state),
        author: isArticleRoute(state.router.activeRoute.name) ? state.article.content.signatures[0]?.authorName : null,
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        duration: getVideoDuration(state, videoId),
        element_id: guessVideoPlayerId(state.router.activeRoute.locale),
        element_location: null,
        element_type: null,
        publish_date: isArticleRoute(state.router.activeRoute.name) ? state.article.content.publishedAt : null,
        element_title: getVideoTitle(state, videoId),
        user_id: state.session?.user?.id ?? '',
        video_id: videoId,
        element_provider: 'Brightcove',
        comment_count: isArticleRoute(state.router.activeRoute.name) ? state.article.content.numberOfComments : null,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: videoStartEpic', logger),
  )

// languageSelectedEpic :: Epic -> Observable Action _
export const languageSelectedEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(CHANGE_LANGUAGE),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([action, _]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'language_selected',
        element_location: 'navigation-menu',
        element_type: 'language_selector',
        event_details: action.locale,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: languageSelectedEpic', logger),
  )

// loginEpic :: Epic -> Observable Action _
export const loginEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(SIGNED_IN),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ action, _]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'login',
        element_location: 'popup',
        element_type: 'login_form',
        method: 'email',
        user_id: action.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: loginEpic', logger),
  )

// logoutEpic :: Epic -> Observable Action _
export const logoutEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(SIGNED_OUT),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(() =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'logout',
        user_id: '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: logoutEpic', logger),
  )

// signUpEpic :: Epic -> Observable Action _
export const signUpEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(SIGNED_UP),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ action, _]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'sign_up',
        element_location: 'popup',
        element_type: 'sign_up_form',
        method: 'email',
        user_id: action.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: signUpEpic', logger),
  )

// contactFormSubmitEpic :: Epic -> Observable Action _
export const contactFormSubmitEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(SUBMITTED),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'contact_form_submit',
        element_location: 'main',
        element_type: 'contact_form',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: contactFormSubmitEpic', logger),
  )

// subscriptionSubmitEpic :: Epic -> Observable Action _
export const subscriptionSubmitEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(NEWSLETTER_SUBMITTED),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'subscription_submit',
        element_location: 'main',
        element_type: 'newsletter_form',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: subscriptionSubmitEpic', logger),
  )

// androidAppDownloadEpic :: Epic -> Observable Action _
export const androidAppDownloadEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(DOWNLOAD_ANDROID_APP),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'app_download',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: getContentTitle(state),
        content_id: getContentId(state),
        author: isArticleRoute(state.router.activeRoute.name) ? state.article.content.signatures[0]?.authorName : null,
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        link_url: process.env.REACT_APP_LINK_GOOGLE_PLAY,
        element_location: 'footer',
        element_type: 'android_app_badge',
        element_details: 'Android',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: androidAppDownloadEpic', logger),
  )

// IOSAppDownloadEpic :: Epic -> Observable Action _
export const IOSAppDownloadEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(DOWNLOAD_IOS_APP),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'app_download',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: getContentTitle(state),
        content_id: getContentId(state),
        author: isArticleRoute(state.router.activeRoute.name) ? state.article.content.signatures[0]?.authorName : null,
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        link_url: process.env.REACT_APP_LINK_APPLE_ITUNES,
        element_location: 'footer',
        element_type: 'ios_app_badge',
        element_details: 'IOS',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: IOSAppDownloadEpic', logger),
  )

// loginFormErrorEpic :: Epic -> Observable Action _
export const loginFormErrorEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(UNVERIFIED_ERROR, ERROR),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'form_error',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: 'Login form',
        element_location: 'popup',
        element_type: 'login_form',
        event_details: 'Error during login',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: loginFormErrorEpic', logger),
  )

// contactFormErrorEpic :: Epic -> Observable Action _
export const contactFormErrorEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(CONTACT_ERROR),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'form_error',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: 'contact form',
        element_location: 'main',
        element_type: 'contact_form',
        event_details: 'Error during contact form submission',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: contactFormErrorEpic', logger),
  )

// newsletterFormErrorEpic :: Epic -> Observable Action _
export const newsletterFormErrorEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(NEWSLETTER_ERROR),
    withLatestFrom(state$),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'form_error',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: 'newsletter form',
        element_location: 'main',
        element_type: 'newsletter_form',
        event_details: 'Error during newsletter form submission',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: newsletterFormErrorEpic', logger),
  )

// elementVisibilityEpic :: Epic -> Observable Action _
export const elementVisibilityEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(OPEN_SIGN_IN),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([_, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'element_visibility',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: getContentTitle(state),
        content_id: getContentId(state),
        author: isArticleRoute(state.router.activeRoute.name) ? state.article.content.signatures[0]?.authorName : null,
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        link_url: '',
        element_id: 'sign-in-form',
        element_location: 'popup',
        element_type: 'popup',
        element_title: 'Sign in',
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: elementVisibilityEpic', logger),
  )

// socialShareEpic :: Epic -> Observable Action _
export const socialShareEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(SHARE_ON_SOCIAL),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(([ _, state ]) => isArticleRoute(state.router.activeRoute.name)),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ action, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'social_share',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: state.article.content.title,
        content_id: state.article.content.id,
        author: state.article.content.signatures[0]?.authorName,
        category_level_1: state.article.content.category.name,
        category_level_2: state.article.content.category.parent?.name ?? null,
        category_level_3: null,
        element_location: 'sticky-share-bar',
        event_details: action.socialName,
        user_id: state.session?.user?.id ?? '',
        page_location: location.pathname,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: socialShareEpic', logger),
  )

// liveStreamPlayRequestEpic :: Epic -> Observable Action _
export const liveStreamPlayRequestEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(INIT_BRIGHTCOVE_VIDEO),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(([{ videoId }, _]) => isLiveVideo(videoId)),
    filter(() => dataLayer()),
    tap(([{ videoId }, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'live_stream_play_request',
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        element_id: guessLiveVideoPlayerId(state.router.activeRoute.locale),
        element_type: new Date(),
        element_title: 'Live Stream',
        publish_date: isArticleRoute(state.router.activeRoute.name) ? state.article.content.publishedAt : null,
        user_id: state.session?.user?.id ?? '',
        video_id: videoId,
        element_provider: 'Brightcove',
        comment_count: isArticleRoute(state.router.activeRoute.name) ? state.article.content.numberOfComments : null,
        page_location: location.pathname,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: liveStreamPlayRequestEpic', logger),
  )

// liveStreamStartEpic :: Epic -> Observable Action _
export const liveStreamStartEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(BRIGHTCOVE_VIDEO_PLAYED),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(([{ videoId }, _]) => isLiveVideo(videoId)),
    filter(() => dataLayer()),
    tap(([{ videoId }, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'live_stream_start',
        category_level_1: getCategoryName(state),
        category_level_2: getParentCategoryName(state),
        category_level_3: null,
        element_id: guessLiveVideoPlayerId(state.router.activeRoute.locale),
        element_type: new Date(),
        element_title: 'Live Stream',
        publish_date: isArticleRoute(state.router.activeRoute.name) ? state.article.content.publishedAt : null,
        user_id: state.session?.user?.id ?? '',
        video_id: videoId,
        element_provider: 'Brightcove',
        comment_count: isArticleRoute(state.router.activeRoute.name) ? state.article.content.numberOfComments : null,
        page_location: location.pathname,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: liveStreamStartEpic', logger),
  )

// addCommentEpic :: Epic -> Observable Action _
export const addCommentEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(RECEIVE_NEW_COMMENT),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(([ _, state ]) => isArticleRoute(state.router.activeRoute.name)),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ _, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'add_comment',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: state.article.content.title,
        content_id: state.article.content.id,
        author: state.article.content.signatures[0]?.authorName,
        category_level_1: state.article.content.category.name,
        category_level_2: state.article.content.category.parent?.name ?? null,
        category_level_3: null,
        publish_date: state.article.content.publishedAt,
        user_id: state.session?.user?.id ?? '',
        comment_count: state.article.content.numberOfComments,
        page_location: location.pathname,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: addCommentEpic', logger),
  )

// saveContentEpic :: Epic -> Observable Action _
export const saveContentEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(BOOKMARK_ADDED),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ action, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'save_content',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: action.article.title,
        content_id: action.article.id,
        author: action.article.signatures[0]?.authorName,
        category_level_1: action.article.category.name,
        category_level_2: action.article.category.parent?.name,
        category_level_3: null,
        element_location: 'sticky-share-bar',
        user_id: state.session?.user?.id ?? '',
        page_location: location.pathname,
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: saveContentEpic', logger),
  )

// fileDownloadEpic :: Epic -> Observable Action _
export const fileDownloadEpic = (action$, state$, { window, dataLayer, location, logger }) =>
  action$.pipe(
    ofType(DOWNLOAD_PDF),
    withLatestFrom(state$),
    filter(([ _, state ]) => state.consentManagement.nonIABVendorsAuthorized),
    filter(() => !isWebView(location)),
    filter(() => dataLayer()),
    tap(([ action, state]) =>
      dataLayer().push({
        event: 'dl_event',
        eventName: 'file_download',
        content_type: getContentType(state.router.activeRoute.name),
        content_title: getContentTitle(state),
        content_id: null,
        author: null,
        category_level_1: null,
        category_level_2: null,
        category_level_3: null,
        link_url: location.pathname,
        element_id: action.filename,
        element_type: action.filename,
        user_id: state.session?.user?.id ?? '',
      }),
    ),
    ignoreElements(),
    logObservableError('googleTags :: fileDownloadEpic', logger),
  )

export default combineEpics(
  loadGtagScriptEpic,
  initializeGtagEpic,
  pageViewEpic,
  searchEpic,
  videoPlayRequestEpic,
  videoStartEpic,
  languageSelectedEpic,
  loginEpic,
  logoutEpic,
  signUpEpic,
  contactFormSubmitEpic,
  subscriptionSubmitEpic,
  androidAppDownloadEpic,
  IOSAppDownloadEpic,
  loginFormErrorEpic,
  contactFormErrorEpic,
  newsletterFormErrorEpic,
  elementVisibilityEpic,
  socialShareEpic,
  liveStreamPlayRequestEpic,
  liveStreamStartEpic,
  addCommentEpic,
  saveContentEpic,
  fileDownloadEpic,
)
